/**
 * Post-game summary card (AoE-style): one time-series chart with a metric
 * dropdown, crucial-moment markers (phase advances, doctrine picks, model
 * ships, eliminations), a full per-player stats table, and save/share.
 *
 * Identity is never color-alone: each player also gets a distinct marker
 * glyph on the chart, and the table header pairs every color chip with the
 * player's name.
 */

import { FACTIONS, PHASE_NAMES, TICKS_PER_SECOND } from '../sim';
import type { GameState, PlayerState, TimelineEvent, TimelineSeries } from '../sim';
import { FACTION_ART } from './faction-art';
import { PLAYER_COLORS } from './sprites';

export function fmtClock(secs: number): string {
  return `${String(Math.floor(secs / 60)).padStart(2, '0')}:${String(secs % 60).padStart(2, '0')}`;
}

const fmtCount = (v: number) => String(Math.round(v));
const fmtMoney = (v: number) => (v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`);

interface Metric {
  key: keyof TimelineSeries;
  label: string;
  fmt: (v: number) => string;
}

const METRICS: Metric[] = [
  { key: 'army', label: 'Army size', fmt: fmtCount },
  { key: 'revenue', label: 'Revenue $/s', fmt: fmtMoney },
  { key: 'budget', label: 'Budget', fmt: fmtMoney },
  { key: 'kills', label: 'Units killed', fmt: fmtCount },
  { key: 'buildings', label: 'Buildings', fmt: fmtCount },
  { key: 'data', label: 'Data harvested', fmt: fmtCount },
  { key: 'serves', label: 'Inferences served', fmt: fmtCount },
];

/** "Player (Faction) vs Player (Faction)" — faction dropped when the name IS
 *  the faction name (bots). */
function matchupLine(s: GameState): string {
  return s.players
    .map((p) => {
      const f = FACTIONS[p.faction].name;
      return p.name === f ? p.name : `${p.name} (${f})`;
    })
    .join(' vs ');
}

/** Deterministic short id of the played game: same seed + same commands →
 *  same fingerprint, so two copies of a shared card can be matched up.
 *  FNV-1a over the identifying facts of the finished state. */
export function gameFingerprint(s: GameState): string {
  const key = JSON.stringify([
    s.seed,
    s.tick,
    s.winner,
    s.players.map((p) => [p.name, p.faction, p.stats]),
  ]);
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

/** "played 2026-07-07 21:15 · seed 3 · #a3f97b21 · room XYZW" — the wall
 *  clock lives outside the (deterministic) sim, so the start time is derived
 *  from now minus the match length; exact while the summary renders at game
 *  end, approximate for replayed states. */
function metaLine(s: GameState, roomCode?: string | null): string {
  const started = new Date(Date.now() - (s.tick / TICKS_PER_SECOND) * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  const when = `${started.getFullYear()}-${pad(started.getMonth() + 1)}-${pad(started.getDate())} ${pad(started.getHours())}:${pad(started.getMinutes())}`;
  const parts = [`played ${when}`, `seed ${s.seed ?? '?'}`, `#${gameFingerprint(s)}`];
  if (roomCode) parts.push(`room ${roomCode}`);
  return parts.join(' · ');
}

/** "Who won and how": headline + cause + match length for the banner.
 *  The cause comes from the last elimination's recorded reason. */
function outcomeLines(s: GameState): { title: string; sub: string; color: string } {
  const len = fmtClock(Math.floor(s.tick / TICKS_PER_SECOND));
  if (s.winner === null || s.winner === -1) {
    return {
      title: s.winner === -1 ? 'Mutual Destruction' : 'Unfinished',
      sub: `match length ${len}`,
      color: '#e6edf3',
    };
  }
  const w = s.players[s.winner];
  const factionName = FACTIONS[w.faction].name;
  const title = w.name === factionName ? `${w.name} wins` : `${w.name} — ${factionName} wins`;
  const elim = [...(s.timeline?.events ?? [])].reverse().find((e) => e.kind === 'eliminated');
  const how = elim ? `${s.players[elim.player].name} out: ${elim.label}` : 'last one standing';
  return { title, sub: `${how} · match length ${len}`, color: PLAYER_COLORS[s.winner] };
}

/** Renders the whole summary block into `root` and wires its controls. */
export function renderSummary(root: HTMLElement, s: GameState, roomCode?: string | null): void {
  if (!s.timeline || !s.players[0]?.stats) {
    root.innerHTML = '';
    return;
  }
  // older snapshots may lack the newer series (buildings/data)
  const metrics = METRICS.filter((m) => Array.isArray(s.timeline.players[0]?.[m.key]));

  const headers = s.players
    .map((p, i) => {
      const factionName = FACTIONS[p.faction].name;
      const sub = p.name === factionName ? '' : `<div class="summary-faction">${escapeHtml(factionName)}</div>`;
      return `<th><span class="summary-chip" style="background:${PLAYER_COLORS[i]}"></span>${escapeHtml(p.name)}${
        s.winner === i ? ' &#128081;' : ''
      }${sub}</th>`;
    })
    .join('');
  const body = statRows(s)
    .map(([label, fn]) => `<tr><td>${label}</td>${s.players.map((p) => `<td>${fn(p)}</td>`).join('')}</tr>`)
    .join('');

  const outcome = outcomeLines(s);
  const art = s.winner !== null && s.winner >= 0 ? FACTION_ART[s.players[s.winner].faction] : null;
  const banner = `
    <div class="summary-winner" style="background-image: linear-gradient(90deg, rgba(8, 10, 16, 0.93) 32%, rgba(8, 10, 16, 0.55))${
      art ? `, url('${art}')` : ''
    }">
      <div class="summary-winner-name" style="color:${outcome.color}">&#128081; ${escapeHtml(outcome.title)}</div>
      <div class="summary-winner-sub">${escapeHtml(outcome.sub)}</div>
      <div class="summary-winner-sub">${escapeHtml(matchupLine(s))}</div>
      <div class="summary-meta">${escapeHtml(metaLine(s, roomCode))}</div>
    </div>`;

  root.innerHTML = `
    ${banner}
    <div class="summary-controls">
      <select id="summary-metric" class="summary-select">
        ${metrics.map((m, i) => `<option value="${m.key}" ${i === 0 ? 'selected' : ''}>${m.label}</option>`).join('')}
      </select>
      <span class="summary-actions">
        <button class="btn-hud" id="summary-save">Save PNG</button>
        <button class="btn-hud" id="summary-share" hidden>Share</button>
      </span>
    </div>
    <figure class="summary-figure"><canvas id="summary-chart"></canvas></figure>
    <table class="summary-table">
      <tr><th></th>${headers}</tr>
      ${body}
    </table>
  `;

  const canvas = root.querySelector<HTMLCanvasElement>('#summary-chart')!;
  // `querySelector<HTMLSelectElement>` trips a spurious TS2344 under this
  // tsconfig's lib mix (see menu.ts qSelect) — same workaround here.
  const select = root.querySelector('#summary-metric') as unknown as HTMLSelectElement;
  const btnSave = root.querySelector<HTMLButtonElement>('#summary-save')!;
  const btnShare = root.querySelector<HTMLButtonElement>('#summary-share')!;

  const current = () => metrics.find((m) => m.key === select.value) ?? metrics[0];
  const redraw = () => drawMetricChart(canvas, s, current());
  select.addEventListener('change', redraw);
  redraw();

  btnSave.addEventListener('click', () => {
    void exportSummaryPng(s, current().key, roomCode).then((blob) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = summaryFileName(s);
      a.click();
      URL.revokeObjectURL(a.href);
    });
  });

  // Share only where the Web Share API can actually deliver a file
  // (mobile browsers, Safari; desktop Chrome behind a flag).
  const probe = new File([''], 'probe.png', { type: 'image/png' });
  if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [probe] })) {
    btnShare.hidden = false;
    btnShare.addEventListener('click', () => {
      void exportSummaryPng(s, current().key, roomCode).then((blob) => {
        const file = new File([blob], summaryFileName(s), { type: 'image/png' });
        // rejection here is almost always the user closing the share sheet
        navigator.share({ files: [file], title: 'Tokenomics RTS match summary' }).catch(() => {});
      });
    });
  }
}

function summaryFileName(s: GameState): string {
  return `tokenomics-${new Date().toISOString().slice(0, 10)}-${gameFingerprint(s)}.png`;
}

function statRows(s: GameState): [string, (p: PlayerState) => string][] {
  const fmtInt = (v: number) => Math.round(v).toLocaleString();
  return [
    ['Final phase', (p) => PHASE_NAMES[p.phase]],
    ['Final budget', (p) => `$${fmtInt(p.budget)}`],
    ['Revenue earned', (p) => `$${fmtInt(p.stats.revenueEarned)}`],
    ['Inferences served', (p) => fmtInt(p.stats.inferencesServed)],
    ['Data harvested', (p) => fmtInt(p.stats.dataHarvested)],
    ['Tokens generated', (p) => fmtInt(p.stats.tokensGenerated)],
    ['Units trained', (p) => fmtInt(p.stats.unitsTrained)],
    ['Units killed', (p) => fmtInt(p.stats.unitsKilled)],
    ['Units lost', (p) => fmtInt(p.stats.unitsLost)],
    ['Buildings built', (p) => fmtInt(p.stats.buildingsBuilt)],
    ['Buildings destroyed', (p) => fmtInt(p.stats.buildingsDestroyed)],
    ['Buildings lost', (p) => fmtInt(p.stats.buildingsLost)],
    ['Cyber ops launched', (p) => fmtInt(p.stats.cyberOpsLaunched)],
  ];
}

// ---------------------------------------------------------------------------
// Chart
// ---------------------------------------------------------------------------

/** Sizes the canvas to its CSS box (dpr-aware) and draws the chart into it. */
function drawMetricChart(canvas: HTMLCanvasElement, s: GameState, metric: Metric): void {
  const cssW = canvas.clientWidth || 320;
  const cssH = canvas.clientHeight || 200;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  drawChartInto(ctx, 0, 0, cssW, cssH, s, metric);
}

/** Core chart painter, shared by the live canvas and the PNG export:
 *  y from zero, recessive grid, 2px player-colored series lines, and
 *  event markers (vertical line + per-player glyph + label). */
function drawChartInto(
  ctx: CanvasRenderingContext2D,
  ox: number,
  oy: number,
  cw: number,
  ch: number,
  s: GameState,
  metric: Metric,
): void {
  const tl = s.timeline;
  const padL = 46;
  const padR = 10;
  const padT = 42; // room for three staggered marker-label rows
  const padB = 18;
  const w = cw - padL - padR;
  const h = ch - padT - padB;
  const ticks = tl.ticks;
  const maxX = ticks[ticks.length - 1] || 1;
  const series = tl.players.map((sp) => sp[metric.key]);
  let maxY = 1;
  for (const vals of series) for (const v of vals) maxY = Math.max(maxY, v);
  maxY *= 1.08; // headroom so the peak doesn't kiss the frame
  const px = (t: number) => ox + padL + (t / maxX) * w;
  const py = (v: number) => oy + padT + h - (v / maxY) * h;

  // grid + y labels
  ctx.font = '10px ui-monospace, Menlo, monospace';
  ctx.lineWidth = 1;
  for (let g = 0; g <= 3; g++) {
    const v = (maxY * g) / 3;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.07)';
    ctx.beginPath();
    ctx.moveTo(ox + padL, py(v));
    ctx.lineTo(ox + cw - padR, py(v));
    ctx.stroke();
    ctx.fillStyle = 'rgba(200, 211, 224, 0.55)';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(metric.fmt(v), ox + padL - 5, py(v));
  }
  // x labels; edge labels hug inward so they don't clip
  ctx.textBaseline = 'top';
  for (const frac of [0, 0.5, 1]) {
    const t = maxX * frac;
    ctx.textAlign = frac === 0 ? 'left' : frac === 1 ? 'right' : 'center';
    ctx.fillText(fmtClock(Math.round(t / TICKS_PER_SECOND)), px(t), oy + padT + h + 5);
  }

  // event markers under the series lines. Labels are packed greedily into
  // three rows: an event takes the first row whose previous label has ended,
  // so clustered events (both bots hitting Walk + doctrine picks together)
  // stack instead of overprinting.
  ctx.font = '9px ui-monospace, Menlo, monospace';
  const rowEnd = [-Infinity, -Infinity, -Infinity];
  const events = (tl.events ?? []).filter((ev) => ev.tick <= maxX);
  for (const ev of events) {
    const x = px(ev.tick);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.13)';
    ctx.beginPath();
    ctx.moveTo(x, oy + padT - 4);
    ctx.lineTo(x, oy + padT + h);
    ctx.stroke();
    let row = rowEnd.findIndex((end) => end <= x - 4);
    if (row === -1) row = rowEnd.indexOf(Math.min(...rowEnd)); // all busy: overlap the emptiest
    const label = markerText(ev);
    rowEnd[row] = x + 10 + ctx.measureText(label).width;
    const rowY = oy + 5 + row * 12;
    drawMarkerGlyph(ctx, x, rowY, ev.player);
    ctx.fillStyle = 'rgba(200, 211, 224, 0.85)';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, x + 6, rowY);
  }

  // series lines
  series.forEach((vals, i) => {
    ctx.strokeStyle = PLAYER_COLORS[i];
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    vals.forEach((v, k) => {
      if (k === 0) ctx.moveTo(px(ticks[k]), py(v));
      else ctx.lineTo(px(ticks[k]), py(v));
    });
    ctx.stroke();
  });
}

function markerText(ev: TimelineEvent): string {
  return ev.kind === 'eliminated' ? '✕' : ev.label;
}

/** Small glyph in the event-player's color; the SHAPE also varies per player
 *  so markers stay tellable-apart under CVD. */
function drawMarkerGlyph(ctx: CanvasRenderingContext2D, x: number, y: number, player: number): void {
  ctx.fillStyle = PLAYER_COLORS[player] ?? '#8b98ad';
  ctx.beginPath();
  const r = 3.5;
  switch (player % 4) {
    case 0: // diamond
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r, y);
      ctx.lineTo(x, y + r);
      ctx.lineTo(x - r, y);
      break;
    case 1: // circle
      ctx.arc(x, y, r, 0, Math.PI * 2);
      break;
    case 2: // square
      ctx.rect(x - r + 0.5, y - r + 0.5, 2 * r - 1, 2 * r - 1);
      break;
    default: // triangle
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r, y + r);
      ctx.lineTo(x - r, y + r);
  }
  ctx.closePath();
  ctx.fill();
}

// ---------------------------------------------------------------------------
// Export (save / share)
// ---------------------------------------------------------------------------

/** Composes the summary — header, winner banner (with faction art when
 *  provided), chart at the chosen metric, stats table — onto an offscreen
 *  canvas ready to be encoded to PNG. */
export function composeSummaryCanvas(
  s: GameState,
  metricKey: keyof TimelineSeries = 'army',
  art: HTMLImageElement | null = null,
  roomCode?: string | null,
): HTMLCanvasElement {
  const metric = METRICS.find((m) => m.key === metricKey) ?? METRICS[0];
  const W = 860;
  const rows = statRows(s);
  const bannerTop = 56;
  const bannerH = 128;
  const chartTop = bannerTop + bannerH + 14;
  const chartH = 240;
  const rowH = 22;
  const tableTop = chartTop + chartH + 26;
  const H = tableTop + (rows.length + 1) * rowH + 24;

  const canvas = document.createElement('canvas');
  const scale = 2; // crisp on retina and when zoomed into a chat thumbnail
  canvas.width = W * scale;
  canvas.height = H * scale;
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(scale, 0, 0, scale, 0, 0);

  ctx.fillStyle = '#10141c';
  ctx.fillRect(0, 0, W, H);

  ctx.fillStyle = '#e6edf3';
  ctx.font = 'bold 20px ui-monospace, Menlo, monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText('TOKENOMICS RTS — MATCH SUMMARY', 24, 20);
  ctx.fillStyle = '#8b98ad';
  ctx.font = '13px ui-monospace, Menlo, monospace';
  ctx.textAlign = 'right';
  ctx.fillText(`${metric.label} over time`, W - 24, 25);

  // winner banner: faction art (cover-cropped), dark gradient, headline
  const outcome = outcomeLines(s);
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(24, bannerTop, W - 48, bannerH, 8);
  ctx.clip();
  ctx.fillStyle = '#161c28';
  ctx.fillRect(24, bannerTop, W - 48, bannerH);
  if (art) drawCover(ctx, art, 24, bannerTop, W - 48, bannerH);
  const grad = ctx.createLinearGradient(24, 0, W - 24, 0);
  grad.addColorStop(0, 'rgba(8, 10, 16, 0.93)');
  grad.addColorStop(0.4, 'rgba(8, 10, 16, 0.82)');
  grad.addColorStop(1, 'rgba(8, 10, 16, 0.45)');
  ctx.fillStyle = grad;
  ctx.fillRect(24, bannerTop, W - 48, bannerH);
  ctx.textAlign = 'left';
  ctx.fillStyle = outcome.color;
  ctx.font = 'bold 22px ui-monospace, Menlo, monospace';
  ctx.fillText(`♛ ${outcome.title}`, 44, bannerTop + 20);
  ctx.fillStyle = '#c9d3e0';
  ctx.font = '13px ui-monospace, Menlo, monospace';
  ctx.fillText(outcome.sub, 44, bannerTop + 52);
  ctx.fillText(matchupLine(s), 44, bannerTop + 74);
  ctx.fillStyle = '#8b98ad';
  ctx.font = '12px ui-monospace, Menlo, monospace';
  ctx.fillText(metaLine(s, roomCode), 44, bannerTop + 98);
  ctx.restore();

  drawChartInto(ctx, 24, chartTop, W - 48, chartH, s, metric);

  // table
  const colLabelW = 190;
  const colW = (W - 48 - colLabelW) / s.players.length;
  const colX = (i: number) => 24 + colLabelW + colW * (i + 1);
  ctx.font = 'bold 13px ui-monospace, Menlo, monospace';
  s.players.forEach((p, i) => {
    ctx.fillStyle = PLAYER_COLORS[i];
    ctx.fillRect(colX(i) - measure(ctx, headerText(s, p, i)) - 16, tableTop + 5, 10, 10);
    ctx.fillStyle = '#e6edf3';
    ctx.textAlign = 'right';
    ctx.fillText(headerText(s, p, i), colX(i), tableTop + 3);
  });
  ctx.font = '13px ui-monospace, Menlo, monospace';
  rows.forEach(([label, fn], r) => {
    const y = tableTop + (r + 1) * rowH + 3;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.beginPath();
    ctx.moveTo(24, y - 4);
    ctx.lineTo(W - 24, y - 4);
    ctx.stroke();
    ctx.fillStyle = '#8b98ad';
    ctx.textAlign = 'left';
    ctx.fillText(label, 24, y);
    ctx.fillStyle = '#c9d3e0';
    ctx.textAlign = 'right';
    s.players.forEach((p, i) => ctx.fillText(fn(p), colX(i), y));
  });

  return canvas;
}

/** Loads the winning faction's key art; resolves null on draw/error so the
 *  export never blocks on a missing image. */
export function loadWinnerArt(s: GameState): Promise<HTMLImageElement | null> {
  if (s.winner === null || s.winner < 0) return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = FACTION_ART[s.players[s.winner!].faction];
  });
}

/** Cover-crop draw (like CSS background-size: cover), biased toward the top
 *  of the source image where the faction art's focal point sits. */
function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
): void {
  const scale = Math.max(dw / img.naturalWidth, dh / img.naturalHeight);
  const sw = dw / scale;
  const sh = dh / scale;
  const sx = (img.naturalWidth - sw) / 2;
  const sy = (img.naturalHeight - sh) * 0.25;
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

export async function exportSummaryPng(
  s: GameState,
  metricKey: keyof TimelineSeries = 'army',
  roomCode?: string | null,
): Promise<Blob> {
  const art = await loadWinnerArt(s);
  const canvas = composeSummaryCanvas(s, metricKey, art, roomCode);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))), 'image/png');
  });
}

function headerText(s: GameState, p: PlayerState, i: number): string {
  return `${p.name}${s.winner === i ? ' ♛' : ''}`;
}

function measure(ctx: CanvasRenderingContext2D, text: string): number {
  return ctx.measureText(text).width;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
